import { useState, useCallback, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  TextField,
  IconButton,
  useTheme,
  Typography,
} from "@mui/material";
import {
  modalBoxStyles,
  sendButtonStyles,
  textFieldStyles,
  popperStyles,
  layoutStyles,
  dayStyles,
  yearButtonStyles,
  buttonsContainerStyles,
  previewButtonStyles,
  closeButtonStyles,
  closeModalBoxStyles,
} from "./styles";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { AdminCreds, MessageInfo } from "../../../types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import dayjs from "dayjs";
import { addInfoMessage, uploadInfoImage } from "../../../axios/admin";
import { colors } from "../../../colors/colors";
import { MessagePreview } from "../message-preview/message-preview";

interface CreateMessageFormProps {
  open: boolean;
  onClose: () => void;
  adminCreds: AdminCreds;
  selectedLogins: string[];
}

interface PreviewFile extends File {
  preview: string;
}

export const CreateMessageForm = ({
  open,
  onClose,
  adminCreds,
  selectedLogins,
}: CreateMessageFormProps) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<MessageInfo>({});
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [sendingResult, setSendingResult] = useState<string | null>(null);
  const [sentImageUrl, setSentImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [send, setSend] = useState(false);

  useEffect(() => {
    if (open) {
      setMessageInfo((prev) => ({ ...prev, login_list: selectedLogins }));
    }
  }, [open, selectedLogins]);

  useEffect(() => setIsModalOpen(open), [open]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setFiles(imageFiles);
    setFormError(null);
    setSentImageUrl(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
  });

  useEffect(() => {
    return () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, [files]);

  const removeImages = () => {
    files.forEach((file) => URL.revokeObjectURL(file.preview));
    setFiles([]);
    setFormError(null);
    setSentImageUrl(null);
  };

  const handleClose = () => {
    setSendingResult(null);
    onClose();
  };

  const sendImage = async () => {
    try {
      const formData = new FormData();
      formData.append("file", files[0]);

      const imageUrl = await uploadInfoImage(formData, adminCreds);
      setSentImageUrl(imageUrl);
      setMessageInfo((prev) => ({ ...prev, image_url: imageUrl }));
      return imageUrl;
    } catch (error) {
      console.error(error);
    }
  };

  const sendMessage = async (imageUrl: string) => {
    try {
      await addInfoMessage(
        {
          ...messageInfo,
          image_url: imageUrl,
          login_list: selectedLogins,
        },
        adminCreds
      );
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (send: boolean) => {
    try {
      setFormError(null);

      if (!selectedLogins || selectedLogins.length === 0) {
        setFormError("No users selected to send message");
        return;
      }

      if (!messageInfo.title) {
        setFormError("Enter message title");
        return;
      }

      if (!messageInfo.text) {
        setFormError("Enter message text");
        return;
      }

      if (files.length === 0) {
        setFormError("Upload an image");
        return;
      }

      setLoading(true);
      const imageUrl = sentImageUrl ?? (await sendImage());
      if (!imageUrl) {
        setFormError("Image sending error");
        return;
      }

      if (
        messageInfo.when_to_send_timestamp &&
        messageInfo.when_to_send_timestamp * 1000 < Date.now()
      ) {
        setFormError("Select a valid time to schedule a message");
        return;
      }

      if (send) {
        await sendMessage(imageUrl);
        setSendingResult("Message sent successfully");
        setMessageInfo({});
        setFormError(null);
        setLoading(false);
        setFiles([]);
      } else {
        setIsModalOpen(false);
        setIsPreviewOpen(true);
      }
    } catch {
      setFormError("Error sending data");
    } finally {
      setLoading(false);
      setSentImageUrl(null);
    }
  };

  useEffect(() => {
    if (send) handleSubmit(true);
  }, [send]);

  if (sendingResult) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box sx={closeModalBoxStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={{
              color: theme.palette.modalColors.headerColor,
            }}
          >
            {sendingResult}
          </CustomTypography>

          <Button
            variant="contained"
            onClick={handleClose}
            sx={closeButtonStyles}
          >
            Close
          </Button>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={isModalOpen} onClose={handleClose}>
        <Box sx={modalBoxStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={{
              color: theme.palette.modalColors.headerColor,
            }}
          >
            Enter message details
          </CustomTypography>

          {formError && (
            <Typography
              variant="body2"
              sx={{
                color: colors.error,
                textAlign: "center",
                fontSize: "1.1em",
              }}
            >
              {formError}
            </Typography>
          )}

          {loading && (
            <Typography
              variant="body2"
              sx={{
                color: colors.black,
                textAlign: "center",
                fontSize: "1.1em",
              }}
            >
              Loading...
            </Typography>
          )}

          <TextField
            fullWidth
            variant="outlined"
            label="Title"
            value={messageInfo.title ?? ""}
            onChange={(e) => {
              setMessageInfo((prev) => ({ ...prev, title: e.target.value }));
              setFormError(null);
            }}
            onFocus={() => setFormError(null)}
            sx={textFieldStyles}
          />

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #aaa",
              padding: 3,
              textAlign: "center",
              borderRadius: 2,
              cursor: "pointer",
              backgroundColor: isDragActive ? "#f5f5f5" : "transparent",
            }}
          >
            <input {...getInputProps()} />
            {files.length === 0 ? (
              <Box
                sx={{
                  color: theme.palette.modalColors.colorLable,
                }}
              >
                <UploadFileIcon fontSize="large" />
                <CustomTypography variant="body1">
                  Drag & drop image here, or click to select
                </CustomTypography>
              </Box>
            ) : (
              <Box
                display="flex"
                gap={2}
                flexWrap="wrap"
                justifyContent="center"
              >
                {files.map((file, index) => (
                  <Box key={index} position="relative" display="inline-block">
                    <img
                      src={file.preview}
                      alt={`Preview ${index}`}
                      style={{ maxHeight: 200, borderRadius: 8 }}
                    />
                    <IconButton
                      onClick={removeImages}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        background: theme.palette.adminPanel.mainBg,
                        transition: "opacity ease 0.3s",
                        "&:hover": {
                          opacity: 0.7,
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            label="Message text"
            multiline
            value={messageInfo.text ?? ""}
            onChange={(e) => {
              setMessageInfo((prev) => ({ ...prev, text: e.target.value }));
              setFormError(null);
            }}
            onFocus={() => setFormError(null)}
            sx={textFieldStyles}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Choose date"
              value={dayjs(
                new Date(
                  messageInfo.when_to_send_timestamp
                    ? messageInfo.when_to_send_timestamp * 1000
                    : Date.now() + 1000 * 60
                )
              )}
              onChange={(newValue) => {
                setFormError(null);
                if (!newValue) {
                  setMessageInfo((prev) => ({
                    ...prev,
                    when_to_send_timestamp: null,
                  }));
                  return;
                }

                setMessageInfo((prev) => ({
                  ...prev,
                  when_to_send_timestamp: Math.floor(newValue.valueOf() / 1000),
                }));
              }}
              minDateTime={dayjs(new Date(Date.now() + 1000 * 60))}
              format="DD.MM.YYYY hh:mm"
              disablePast
              open={isDatePickerOpen}
              onOpen={() => {
                setIsDatePickerOpen(true);
                setFormError(null);
              }}
              onClose={() => setIsDatePickerOpen(false)}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  onClick: () => setIsDatePickerOpen(true),
                  sx: textFieldStyles,
                },
                popper: {
                  sx: popperStyles,
                },
                layout: {
                  sx: layoutStyles,
                },
                day: {
                  sx: dayStyles,
                },
                yearButton: {
                  sx: yearButtonStyles,
                },
              }}
            />
          </LocalizationProvider>

          <Box sx={buttonsContainerStyles}>
            <Button
              variant="outlined"
              onClick={() => handleSubmit(false)}
              sx={previewButtonStyles}
            >
              Preview Message
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSubmit(true)}
              sx={sendButtonStyles}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Modal>
      <MessagePreview
        open={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setIsModalOpen(true);
        }}
        messageInfo={messageInfo}
        adminCreds={adminCreds}
        setSend={() => setSend(true)}
      />
    </>
  );
};
